/**
 * ONE compact "how long ago" helper, for every surface that renders one.
 *
 * ── Why this exists ───────────────────────────────────────────────────────
 *
 * There were three near-identical private copies: `timeAgo` in
 * RecentlyViewedMenu, `timeAgo` in NotificationBell, and `shortRelTime` in
 * ProspectsPage. The ladder (just now / Xm / Xh / Xd) was byte-identical in all
 * three; only the TAIL differed, so the same age rendered differently depending
 * on which surface you were looking at, and a fix to one reached none of the
 * others. The zone bug fixed in NotificationBell — a `>7d` date rendered in the
 * browser's zone rather than the viewer's — is still sitting in
 * RecentlyViewedMenu's copy today, which is exactly what duplication costs.
 *
 * ── The tail is a parameter, not a fork ───────────────────────────────────
 *
 * The two legitimate tails are kept, because they answer different questions:
 *
 *   · `beyond` — "older than N days, show the DATE instead". What a list of
 *     recently-touched things wants: past a week, the weekday-relative phrasing
 *     stops helping and an actual date is more use. Callers pass their own
 *     renderer so the date goes through the viewer's display zone.
 *   · omitted — the calendar tail ("3mo ago", "2y ago"). What an AGE wants: a
 *     prospect nobody has contacted in four months is a fact about the duration,
 *     and a date would make the reader do the subtraction.
 *
 * ── Calendar math, not day arithmetic ─────────────────────────────────────
 *
 * ⚠️ The months/years tail counts CALENDAR months, not `days / 30`. The naive
 * version had a visible bug: 360 days is 12 "30-day months", so an eleven-and-
 * a-bit-month-old record read "1y ago" before it was a year old at all. Real
 * month stepping (with a day-of-month correction) reports 11mo there.
 *
 * Years deliberately floor — 23 months reads "1y ago" — which is the same
 * convention date-fns and every other relative formatter uses.
 */

import { logger } from "../services/logger.service";

export interface RelativeAgeOptions {
  /** Injectable clock. Defaults to `Date.now()`; tests pass a fixed instant. */
  now?: number;
  /**
   * Replace the calendar tail with a date once the value is older than
   * `afterDays`. `render` receives the original ISO string so the caller can
   * format it in the viewer's display zone.
   */
  beyond?: { afterDays: number; render: (iso: string) => string };
}

/** See the catch below — this failure is systemic, so it is reported once. */
let rendererFailureLogged = false;

/** `""` for a missing or unparseable value — every caller renders that as empty. */
export function relativeAge(iso: string | null | undefined, opts: RelativeAgeOptions = {}): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";

  const now = opts.now ?? Date.now();
  // Clamped at 0: a clock skew or a server timestamp a few seconds in the future
  // must read "just now", never a negative duration.
  const secs = Math.max(0, Math.floor((now - then) / 1000));
  if (secs < 60) return "just now";

  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;

  const days = Math.floor(hrs / 24);
  if (opts.beyond) {
    if (days < opts.beyond.afterDays) return `${days}d ago`;
    // ⚠️ THE CALLER'S RENDERER CAN THROW, and this runs inside render.
    // Every caller passes formatInstant, which throws on a date-only value
    // rather than guessing midnight. ProspectsPage guards that at its own call
    // site because a throw there unmounts the whole grid — but the notification
    // bell and recently-viewed reach the identical hazard THROUGH this helper,
    // where they cannot guard it. Catching once here covers all of them, and
    // degrades to the day count, which is the honest answer for a value the
    // renderer could not format. (Not reachable today: both fields are server
    // timestamptz. That is exactly why it needs a guard rather than a test.)
    try {
      return opts.beyond.render(iso);
    } catch (err) {
      // ⚠️ NOT a silent catch. The fallback below is correct, but if this ever
      // fires SYSTEMICALLY — a bad zone, or a column that starts arriving
      // date-only — every affected row quietly degrades to "Nd ago" and looks
      // merely stale rather than broken. Swallowing it would leave no way to
      // tell those two apart. Warn, then degrade.
      // ⚠️ ONCE per session, not once per row per render. This runs inside
      // render, and the failure it reports is systemic by nature — a bad zone or
      // a column that starts arriving date-only hits EVERY row, on every
      // re-render. Unthrottled that is an Application Insights event per row per
      // render, flooding ingestion over a cosmetic degradation. One warning
      // carries the same signal.
      if (!rendererFailureLogged) {
        rendererFailureLogged = true;
        logger.warn("[relativeAge] the caller's date renderer threw; falling back to days", {
          iso,
          error: err instanceof Error ? err.message : String(err),
        });
      }
      return `${days}d ago`;
    }
  }
  if (days < 30) return `${days}d ago`;

  // ⚠️ `months < 1` FALLS BACK TO DAYS rather than rounding up to "1mo ago".
  // 30 days is not necessarily a calendar month: 17 Aug → 16 Sep is 30 days but
  // the day-of-month has not come round, so calendarMonthsBetween is 0. An
  // earlier version clamped with Math.max(1, months) and reported "1mo ago"
  // there — the exact mirror of the "360 days is not a year" bug this helper was
  // written to fix, one scale down. Saying "30d ago" is simply true.
  const months = calendarMonthsBetween(new Date(then), new Date(now));
  if (months < 1) return `${days}d ago`;
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

/**
 * Whole calendar months from `from` to `to`, not counting an incomplete final month.
 *
 * Counts in the RUNTIME's zone (getFullYear/getMonth/getDate), which is a known
 * and accepted approximation: both instants are read in the same zone, so the
 * difference only shifts within a day of a month boundary, and the result is a
 * duration ("3mo ago") rather than a date anyone can act on. A zone-correct
 * version would need the display zone threaded through every caller, which is
 * not worth it for a coarse tail — but do NOT copy this pattern for anything
 * that renders an actual DATE; those go through formatInstant.
 */
function calendarMonthsBetween(from: Date, to: Date): number {
  let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  // The final month has not elapsed until the day-of-month is reached — but an
  // anchor day the target month does not HAVE is reached on that month's last
  // day, not never. Comparing against `from.getDate()` raw meant a record
  // stamped on the 31st stayed a month young for the whole of every shorter
  // month: 2026-01-31 -> 2026-04-30 (89 days) read "2mo" while 2026-01-30 ->
  // 2026-04-30 read "3mo". date-fns clamps for the same reason.
  const lastDayOfTargetMonth = new Date(to.getFullYear(), to.getMonth() + 1, 0).getDate();
  const anniversaryDay = Math.min(from.getDate(), lastDayOfTargetMonth);
  if (to.getDate() < anniversaryDay) months -= 1;
  return Math.max(0, months);
}
